<template>
    <div class="caloric-calculator">
        <div class="calc-header">
            <h3>Smart Caloric Needs</h3>
            <div class="calc-badge" :class="activityLevel">
                {{ activityLevelLabel }}
            </div>
        </div>

        <!-- Activity Context Summary -->
        <div class="activity-summary">
            <div class="activity-icon" :class="activityContext">
                <span>{{ contextIcon }}</span>
            </div>
            <div class="activity-info">
                <span class="activity-label">{{ contextLabel }}</span>
                <span class="activity-date">{{ formatDate(date) }}</span>
            </div>
        </div>

        <!-- Training/GPS Data Integration -->
        <div v-if="trainingData" class="training-data">
            <div class="data-header">
                <span class="data-icon">📊</span>
                <span>Training Load Data</span>
            </div>

            <div class="metrics-grid">
                <div class="metric-card">
                    <span class="metric-value">{{ trainingData.distance_km?.toFixed(1) || 0 }}</span>
                    <span class="metric-label">Distance (km)</span>
                </div>
                <div class="metric-card">
                    <span class="metric-value">{{ trainingData.duration_mins || 0 }}</span>
                    <span class="metric-label">Duration (min)</span>
                </div>
                <div class="metric-card">
                    <span class="metric-value">{{ trainingData.avg_speed?.toFixed(1) || 0 }}</span>
                    <span class="metric-label">Avg Speed (km/h)</span>
                </div>
                <div class="metric-card">
                    <span class="metric-value">{{ trainingData.high_intensity_mins || 0 }}</span>
                    <span class="metric-label">High Intensity (min)</span>
                </div>
            </div>

            <div class="load-indicator">
                <div class="load-bar">
                    <div class="load-fill" :style="{ width: loadPercentage + '%' }" :class="loadClass"></div>
                </div>
                <div class="load-labels">
                    <span>Low</span>
                    <span>Medium</span>
                    <span>High</span>
                </div>
            </div>
        </div>

        <!-- No Training Data - Manual Input -->
        <div v-else class="manual-input">
            <p class="input-label">No GPS data available. Estimate your activity:</p>

            <div class="activity-selector">
                <button v-for="option in activityOptions" :key="option.value" class="activity-option"
                    :class="{ selected: selectedActivity === option.value }" @click="selectedActivity = option.value">
                    <span class="option-icon">{{ option.icon }}</span>
                    <span class="option-label">{{ option.label }}</span>
                    <span class="option-multiplier">×{{ option.multiplier }}</span>
                </button>
            </div>

            <div class="duration-input">
                <label>Training Duration (minutes)</label>
                <div class="duration-slider">
                    <input type="range" v-model.number="manualDuration" min="0" max="180" step="15" />
                    <span class="duration-value">{{ manualDuration }} min</span>
                </div>
            </div>
        </div>

        <!-- Caloric Calculation Results -->
        <div class="calculation-results">
            <div class="result-header">
                <span class="result-icon">🔥</span>
                <h4>Today's Caloric Needs</h4>
            </div>

            <div class="calorie-breakdown">
                <!-- Base Metabolic -->
                <div class="breakdown-row">
                    <span class="row-label">Base Metabolic Rate (BMR)</span>
                    <span class="row-value">{{ calculations.bmr }} kcal</span>
                </div>

                <!-- Activity Factor -->
                <div class="breakdown-row">
                    <span class="row-label">Activity Level Factor</span>
                    <span class="row-value">× {{ calculations.activityFactor }}</span>
                </div>

                <!-- Training Expenditure -->
                <div class="breakdown-row highlight">
                    <span class="row-label">Training/Activity Burn</span>
                    <span class="row-value">+ {{ calculations.trainingCalories }} kcal</span>
                </div>

                <!-- Context Adjustment -->
                <div class="breakdown-row" v-if="calculations.contextAdjustment !== 0">
                    <span class="row-label">{{ contextLabel }} Adjustment</span>
                    <span class="row-value" :class="{ positive: calculations.contextAdjustment > 0 }">
                        {{ calculations.contextAdjustment > 0 ? '+' : '' }}{{ calculations.contextAdjustment }} kcal
                    </span>
                </div>

                <div class="breakdown-divider"></div>

                <!-- Total -->
                <div class="breakdown-row total">
                    <span class="row-label">Total Daily Needs</span>
                    <span class="row-value">{{ calculations.totalCalories }} kcal</span>
                </div>
            </div>

            <!-- Macro Distribution -->
            <div class="macro-distribution">
                <h5>Recommended Macros</h5>

                <div class="macro-bars">
                    <div class="macro-bar carbs">
                        <div class="bar-header">
                            <span class="macro-name">Carbohydrates</span>
                            <span class="macro-amount">{{ calculations.macros.carbs }}g</span>
                        </div>
                        <div class="bar-visual">
                            <div class="bar-fill" :style="{ width: calculations.macros.carbsPercent + '%' }"></div>
                        </div>
                        <span class="macro-percent">{{ calculations.macros.carbsPercent }}%</span>
                    </div>

                    <div class="macro-bar protein">
                        <div class="bar-header">
                            <span class="macro-name">Protein</span>
                            <span class="macro-amount">{{ calculations.macros.protein }}g</span>
                        </div>
                        <div class="bar-visual">
                            <div class="bar-fill" :style="{ width: calculations.macros.proteinPercent + '%' }"></div>
                        </div>
                        <span class="macro-percent">{{ calculations.macros.proteinPercent }}%</span>
                    </div>

                    <div class="macro-bar fat">
                        <div class="bar-header">
                            <span class="macro-name">Fat</span>
                            <span class="macro-amount">{{ calculations.macros.fat }}g</span>
                        </div>
                        <div class="bar-visual">
                            <div class="bar-fill" :style="{ width: calculations.macros.fatPercent + '%' }"></div>
                        </div>
                        <span class="macro-percent">{{ calculations.macros.fatPercent }}%</span>
                    </div>
                </div>
            </div>

            <!-- Per Kg Breakdown -->
            <div class="per-kg-info" v-if="weight">
                <div class="pkg-item">
                    <span class="pkg-label">Carbs</span>
                    <span class="pkg-value">{{ (calculations.macros.carbs / weight).toFixed(1) }}g/kg</span>
                </div>
                <div class="pkg-item">
                    <span class="pkg-label">Protein</span>
                    <span class="pkg-value">{{ (calculations.macros.protein / weight).toFixed(1) }}g/kg</span>
                </div>
                <div class="pkg-item">
                    <span class="pkg-label">Fat</span>
                    <span class="pkg-value">{{ (calculations.macros.fat / weight).toFixed(1) }}g/kg</span>
                </div>
            </div>
        </div>

        <!-- Meal Distribution Suggestion -->
        <div class="meal-distribution">
            <h5>Suggested Meal Distribution</h5>

            <div class="meal-timeline">
                <div v-for="meal in mealDistribution" :key="meal.type" class="meal-block" :class="meal.type">
                    <div class="meal-time">{{ meal.time }}</div>
                    <div class="meal-name">{{ meal.name }}</div>
                    <div class="meal-calories">{{ meal.calories }} kcal</div>
                    <div class="meal-macros">
                        <span>C: {{ meal.carbs }}g</span>
                        <span>P: {{ meal.protein }}g</span>
                        <span>F: {{ meal.fat }}g</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Action Button -->
        <button class="apply-btn" @click="applyToDay">
            Apply to Today's Plan
        </button>
    </div>
</template>

<script>
export default {
    name: "TrainingLoadCaloricCalculator",

    props: {
        userId: {
            type: [Number, String],
            required: true,
        },
        weight: {
            type: Number,
            default: 70,
        },
        height: {
            type: Number,
            default: 175,
        },
        age: {
            type: Number,
            default: 18,
        },
        gender: {
            type: String,
            default: "male",
        },
        date: {
            type: String,
            default: () => new Date().toISOString().split("T")[0],
        },
        activityContext: {
            type: String,
            default: "training_day",
        },
        trainingData: {
            type: Object,
            default: null,
        },
        matchTime: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            selectedActivity: "moderate",
            manualDuration: 60,
            activityOptions: [
                { value: "rest", label: "Rest Day", icon: "🛋️", multiplier: 1.2 },
                { value: "light", label: "Light Training", icon: "🚶", multiplier: 1.4 },
                { value: "moderate", label: "Normal Training", icon: "🏃", multiplier: 1.6 },
                { value: "intense", label: "High Intensity", icon: "⚡", multiplier: 1.8 },
                { value: "match", label: "Match Day", icon: "⚽", multiplier: 2.0 },
            ],
        };
    },

    computed: {
        contextLabel() {
            const labels = {
                match_day: "Match Day",
                carb_loading: "Carb Loading (MD-1)",
                preparation: "Preparation (MD-2)",
                recovery_day: "Recovery Day",
                training_day: "Training Day",
                rest_day: "Rest Day",
            };
            return labels[this.activityContext] || "Training Day";
        },

        contextIcon() {
            const icons = {
                match_day: "⚽",
                carb_loading: "🍝",
                preparation: "📋",
                recovery_day: "🔄",
                training_day: "🏃",
                rest_day: "🛋️",
            };
            return icons[this.activityContext] || "🏃";
        },

        activityLevel() {
            if (this.activityContext === "match_day") return "high";
            if (this.activityContext === "rest_day") return "low";
            if (this.trainingData) {
                const load = this.calculateTrainingLoad();
                if (load > 70) return "high";
                if (load > 40) return "medium";
                return "low";
            }
            return this.selectedActivity === "intense" || this.selectedActivity === "match"
                ? "high"
                : this.selectedActivity === "rest"
                    ? "low"
                    : "medium";
        },

        activityLevelLabel() {
            const labels = { high: "High Activity", medium: "Medium Activity", low: "Low Activity" };
            return labels[this.activityLevel];
        },

        loadPercentage() {
            if (!this.trainingData) return 50;
            return Math.min(100, this.calculateTrainingLoad());
        },

        loadClass() {
            if (this.loadPercentage > 70) return "high";
            if (this.loadPercentage > 40) return "medium";
            return "low";
        },

        calculations() {
            // Calculate BMR using Mifflin-St Jeor equation
            let bmr;
            if (this.gender === "male") {
                bmr = 10 * this.weight + 6.25 * this.height - 5 * this.age + 5;
            } else {
                bmr = 10 * this.weight + 6.25 * this.height - 5 * this.age - 161;
            }
            bmr = Math.round(bmr);

            // Activity factor based on context or selection
            let activityFactor = this.getActivityFactor();

            // Calculate training calories
            let trainingCalories = this.calculateTrainingCalories();

            // Context-specific adjustments
            let contextAdjustment = this.getContextAdjustment();

            // Total calories
            let totalCalories = Math.round(bmr * activityFactor + trainingCalories + contextAdjustment);

            // Calculate macros based on context
            const macros = this.calculateMacros(totalCalories);

            return {
                bmr,
                activityFactor: activityFactor.toFixed(2),
                trainingCalories,
                contextAdjustment,
                totalCalories,
                macros,
            };
        },

        mealDistribution() {
            const total = this.calculations.totalCalories;
            const macros = this.calculations.macros;

            // Distribution based on context
            let distribution;
            if (this.activityContext === "match_day") {
                distribution = [
                    { type: "breakfast", name: "Breakfast", time: "08:00", percent: 0.20 },
                    { type: "pre_match", name: "Pre-Match Meal", time: this.getPreMatchTime(), percent: 0.30 },
                    { type: "snack", name: "Pre-Match Snack", time: this.getPreMatchSnackTime(), percent: 0.10 },
                    { type: "post_match", name: "Recovery Meal", time: this.getPostMatchTime(), percent: 0.25 },
                    { type: "dinner", name: "Dinner", time: "20:00", percent: 0.15 },
                ];
            } else {
                distribution = [
                    { type: "breakfast", name: "Breakfast", time: "08:00", percent: 0.25 },
                    { type: "lunch", name: "Lunch", time: "12:30", percent: 0.30 },
                    { type: "snack", name: "Afternoon Snack", time: "15:30", percent: 0.10 },
                    { type: "dinner", name: "Dinner", time: "19:00", percent: 0.25 },
                    { type: "evening", name: "Evening Snack", time: "21:00", percent: 0.10 },
                ];
            }

            return distribution.map((meal) => ({
                ...meal,
                calories: Math.round(total * meal.percent),
                carbs: Math.round(macros.carbs * meal.percent),
                protein: Math.round(macros.protein * meal.percent),
                fat: Math.round(macros.fat * meal.percent),
            }));
        },
    },

    methods: {
        formatDate(dateStr) {
            const date = new Date(dateStr);
            return date.toLocaleDateString("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
            });
        },

        getActivityFactor() {
            if (this.activityContext === "match_day") return 1.9;
            if (this.activityContext === "carb_loading") return 1.4;
            if (this.activityContext === "rest_day") return 1.2;
            if (this.activityContext === "recovery_day") return 1.3;

            if (this.trainingData) {
                const load = this.calculateTrainingLoad();
                if (load > 70) return 1.8;
                if (load > 40) return 1.6;
                return 1.4;
            }

            const option = this.activityOptions.find((o) => o.value === this.selectedActivity);
            return option?.multiplier || 1.6;
        },

        calculateTrainingLoad() {
            if (!this.trainingData) return 50;

            const distance = this.trainingData.distance_km || 0;
            const duration = this.trainingData.duration_mins || 0;
            const hiMins = this.trainingData.high_intensity_mins || 0;
            const avgSpeed = this.trainingData.avg_speed || 0;

            // Weighted calculation
            const distanceScore = Math.min(30, distance * 3);
            const durationScore = Math.min(30, duration / 3);
            const intensityScore = Math.min(25, hiMins * 1.5);
            const speedScore = Math.min(15, avgSpeed);

            return Math.round(distanceScore + durationScore + intensityScore + speedScore);
        },

        calculateTrainingCalories() {
            if (this.trainingData) {
                // Use MET values for football training
                const duration = this.trainingData.duration_mins || 0;
                const intensity = this.trainingData.high_intensity_mins || 0;

                // MET values: low=5, moderate=7, high=10
                const lowMins = Math.max(0, duration - intensity);
                const highMins = intensity;

                const lowCal = (5 * 3.5 * this.weight * lowMins) / 200;
                const highCal = (10 * 3.5 * this.weight * highMins) / 200;

                return Math.round(lowCal + highCal);
            }

            // Manual calculation
            const metValues = {
                rest: 1.5,
                light: 4,
                moderate: 7,
                intense: 10,
                match: 10,
            };
            const met = metValues[this.selectedActivity] || 7;
            return Math.round((met * 3.5 * this.weight * this.manualDuration) / 200);
        },

        getContextAdjustment() {
            // Additional calorie adjustments based on context
            const adjustments = {
                match_day: 300, // Extra for match performance
                carb_loading: 200, // Extra for glycogen loading
                recovery_day: -100, // Slightly less on recovery
                rest_day: -200, // Less on complete rest
                preparation: 0,
                training_day: 0,
            };
            return adjustments[this.activityContext] || 0;
        },

        calculateMacros(totalCalories) {
            // Macro ratios based on context
            let carbPercent, proteinPercent, fatPercent;

            switch (this.activityContext) {
                case "match_day":
                    carbPercent = 60;
                    proteinPercent = 20;
                    fatPercent = 20;
                    break;
                case "carb_loading":
                    carbPercent = 65;
                    proteinPercent = 18;
                    fatPercent = 17;
                    break;
                case "recovery_day":
                    carbPercent = 45;
                    proteinPercent = 30;
                    fatPercent = 25;
                    break;
                case "rest_day":
                    carbPercent = 40;
                    proteinPercent = 30;
                    fatPercent = 30;
                    break;
                default:
                    carbPercent = 55;
                    proteinPercent = 25;
                    fatPercent = 20;
            }

            const carbCals = totalCalories * (carbPercent / 100);
            const proteinCals = totalCalories * (proteinPercent / 100);
            const fatCals = totalCalories * (fatPercent / 100);

            return {
                carbs: Math.round(carbCals / 4),
                protein: Math.round(proteinCals / 4),
                fat: Math.round(fatCals / 9),
                carbsPercent: carbPercent,
                proteinPercent: proteinPercent,
                fatPercent: fatPercent,
            };
        },

        getPreMatchTime() {
            if (!this.matchTime) return "12:00";
            const match = new Date(`2000-01-01 ${this.matchTime}`);
            match.setHours(match.getHours() - 4);
            return match.toTimeString().slice(0, 5);
        },

        getPreMatchSnackTime() {
            if (!this.matchTime) return "14:00";
            const match = new Date(`2000-01-01 ${this.matchTime}`);
            match.setHours(match.getHours() - 2);
            return match.toTimeString().slice(0, 5);
        },

        getPostMatchTime() {
            if (!this.matchTime) return "18:00";
            const match = new Date(`2000-01-01 ${this.matchTime}`);
            match.setHours(match.getHours() + 2);
            return match.toTimeString().slice(0, 5);
        },

        applyToDay() {
            this.$emit("apply", {
                date: this.date,
                totalCalories: this.calculations.totalCalories,
                macros: this.calculations.macros,
                mealDistribution: this.mealDistribution,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.caloric-calculator {
    background: white;
    border-radius: 12px;
    padding: 16px;
}

.calc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }

    .calc-badge {
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 600;

        &.low {
            background: rgba(40, 167, 69, 0.15);
            color: #28a745;
        }

        &.medium {
            background: rgba(255, 193, 7, 0.15);
            color: #cc9a06;
        }

        &.high {
            background: rgba(220, 53, 69, 0.15);
            color: #dc3545;
        }
    }
}

.activity-summary {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 10px;
    margin-bottom: 16px;

    .activity-icon {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.4rem;

        &.match_day {
            background: linear-gradient(135deg, #dc3545, #fd7e14);
        }

        &.carb_loading {
            background: linear-gradient(135deg, #ffc107, #fd7e14);
        }

        &.training_day {
            background: linear-gradient(135deg, #007bff, #17a2b8);
        }

        &.recovery_day {
            background: linear-gradient(135deg, #28a745, #20c997);
        }

        &.rest_day {
            background: linear-gradient(135deg, #6c757d, #adb5bd);
        }
    }

    .activity-info {
        .activity-label {
            display: block;
            font-weight: 600;
            font-size: 1rem;
        }

        .activity-date {
            font-size: 0.85rem;
            color: #666;
        }
    }
}

.training-data {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 16px;

    .data-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        font-weight: 600;
        font-size: 0.9rem;
    }

    .metrics-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px;
        margin-bottom: 12px;

        .metric-card {
            text-align: center;
            padding: 8px;
            background: white;
            border-radius: 8px;

            .metric-value {
                display: block;
                font-size: 1.1rem;
                font-weight: 700;
                color: #007bff;
            }

            .metric-label {
                font-size: 0.65rem;
                color: #666;
            }
        }
    }

    .load-indicator {
        .load-bar {
            height: 8px;
            background: #e0e0e0;
            border-radius: 4px;
            overflow: hidden;

            .load-fill {
                height: 100%;
                border-radius: 4px;
                transition: width 0.3s;

                &.low {
                    background: linear-gradient(90deg, #28a745, #20c997);
                }

                &.medium {
                    background: linear-gradient(90deg, #ffc107, #fd7e14);
                }

                &.high {
                    background: linear-gradient(90deg, #fd7e14, #dc3545);
                }
            }
        }

        .load-labels {
            display: flex;
            justify-content: space-between;
            margin-top: 4px;
            font-size: 0.7rem;
            color: #999;
        }
    }
}

.manual-input {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 16px;

    .input-label {
        margin: 0 0 12px 0;
        font-size: 0.9rem;
        color: #666;
    }

    .activity-selector {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;

        .activity-option {
            flex: 1;
            min-width: calc(50% - 4px);
            padding: 10px 8px;
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            background: white;
            cursor: pointer;
            text-align: center;
            transition: all 0.2s;

            &:hover {
                border-color: #ccc;
            }

            &.selected {
                border-color: #28a745;
                background: rgba(40, 167, 69, 0.05);
            }

            .option-icon {
                display: block;
                font-size: 1.3rem;
                margin-bottom: 4px;
            }

            .option-label {
                display: block;
                font-size: 0.75rem;
                font-weight: 600;
            }

            .option-multiplier {
                display: block;
                font-size: 0.7rem;
                color: #999;
            }
        }
    }

    .duration-input {
        label {
            display: block;
            font-size: 0.85rem;
            font-weight: 500;
            margin-bottom: 8px;
        }

        .duration-slider {
            display: flex;
            align-items: center;
            gap: 12px;

            input[type="range"] {
                flex: 1;
                height: 6px;
                border-radius: 3px;
                appearance: none;
                background: #e0e0e0;

                &::-webkit-slider-thumb {
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: #28a745;
                    cursor: pointer;
                }
            }

            .duration-value {
                font-weight: 600;
                min-width: 60px;
                text-align: right;
            }
        }
    }
}

.calculation-results {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;

    .result-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 14px;

        .result-icon {
            font-size: 1.3rem;
        }

        h4 {
            margin: 0;
            font-size: 1rem;
        }
    }

    .calorie-breakdown {
        background: white;
        border-radius: 10px;
        padding: 12px;
        margin-bottom: 16px;

        .breakdown-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 0.85rem;

            .row-label {
                color: #666;
            }

            .row-value {
                font-weight: 600;

                &.positive {
                    color: #28a745;
                }
            }

            &.highlight {
                background: rgba(253, 126, 20, 0.1);
                margin: 0 -12px;
                padding: 8px 12px;
                border-radius: 6px;

                .row-value {
                    color: #fd7e14;
                }
            }

            &.total {
                .row-label {
                    font-weight: 700;
                    color: #333;
                }

                .row-value {
                    font-size: 1.2rem;
                    color: #28a745;
                }
            }
        }

        .breakdown-divider {
            height: 1px;
            background: #eee;
            margin: 8px 0;
        }
    }

    .macro-distribution {
        h5 {
            margin: 0 0 12px 0;
            font-size: 0.9rem;
            font-weight: 600;
        }

        .macro-bars {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .macro-bar {
            display: grid;
            grid-template-columns: 1fr auto;
            grid-template-rows: auto auto;
            gap: 4px 8px;
            align-items: center;

            .bar-header {
                display: flex;
                justify-content: space-between;

                .macro-name {
                    font-size: 0.8rem;
                    font-weight: 500;
                }

                .macro-amount {
                    font-size: 0.85rem;
                    font-weight: 700;
                }
            }

            .bar-visual {
                height: 8px;
                background: #e0e0e0;
                border-radius: 4px;
                overflow: hidden;

                .bar-fill {
                    height: 100%;
                    border-radius: 4px;
                }
            }

            .macro-percent {
                grid-row: span 2;
                font-size: 0.75rem;
                color: #666;
            }

            &.carbs {
                .macro-amount {
                    color: #cc9a06;
                }

                .bar-fill {
                    background: linear-gradient(90deg, #ffc107, #ffda6a);
                }
            }

            &.protein {
                .macro-amount {
                    color: #dc3545;
                }

                .bar-fill {
                    background: linear-gradient(90deg, #dc3545, #ea868f);
                }
            }

            &.fat {
                .macro-amount {
                    color: #28a745;
                }

                .bar-fill {
                    background: linear-gradient(90deg, #28a745, #75b798);
                }
            }
        }
    }

    .per-kg-info {
        display: flex;
        justify-content: space-around;
        margin-top: 12px;
        padding: 10px;
        background: white;
        border-radius: 8px;

        .pkg-item {
            text-align: center;

            .pkg-label {
                display: block;
                font-size: 0.7rem;
                color: #666;
            }

            .pkg-value {
                font-weight: 700;
                font-size: 0.9rem;
            }
        }
    }
}

.meal-distribution {
    margin-bottom: 16px;

    h5 {
        margin: 0 0 12px 0;
        font-size: 0.9rem;
        font-weight: 600;
        color: #666;
    }

    .meal-timeline {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .meal-block {
        display: grid;
        grid-template-columns: 50px 1fr auto;
        grid-template-rows: auto auto;
        gap: 2px 12px;
        padding: 10px;
        border-radius: 8px;
        background: #f8f9fa;

        .meal-time {
            grid-row: span 2;
            font-size: 0.85rem;
            font-weight: 600;
            color: #666;
            display: flex;
            align-items: center;
        }

        .meal-name {
            font-weight: 600;
            font-size: 0.9rem;
        }

        .meal-calories {
            font-weight: 700;
            color: #fd7e14;
        }

        .meal-macros {
            grid-column: 2 / -1;
            font-size: 0.75rem;
            color: #666;

            span {
                margin-right: 12px;
            }
        }

        &.pre_match,
        &.post_match {
            background: rgba(220, 53, 69, 0.1);
            border-left: 3px solid #dc3545;
        }
    }
}

.apply-btn {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
}
</style>
